import { Box } from '@modusign/design-system';
import { useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';

import { createUseResponsiveValue } from '@hooks';

import {
  Breakpoint,
  DesktopFormRecommendationSheet,
  FORM_RECOMMENDATION_LOCAL_STORAGE_KEY,
  MobileFormRecommendationSheet,
} from '@app/features/formRecommendation';
import { getAdobeTargetOffer } from '@app/libs/analytics/adobe';

const CONSTRAINT = {
  SHEET: {
    DESKTOP_MAX_HEIGHT: 234,
    MOBILE_MAX_HEIGHT: 316,
  },
} as const;

const useResponsiveValue = createUseResponsiveValue([385, 768, 960, 1440, 1920, 2560]);

function FormRecommendationSheet() {
  const breakpoint: Breakpoint = useResponsiveValue([
    'mobile-xs',
    'mobile',
    'sm',
    'md',
    'lg',
    'xl',
  ]);

  const [isOpened, setIsOpened] = useLocalStorage(FORM_RECOMMENDATION_LOCAL_STORAGE_KEY, true);
  const [formIds, setFormIds] = useState<string[]>();
  const responsiveFormIds = useMemo(() => {
    // 화면 가로 길이에 따라 따라 보여줄 서식의 개수를 정합니다.
    if (!formIds) return [];
    switch (breakpoint) {
      case 'mobile-xs':
      case 'mobile':
      case 'sm':
        return formIds.slice(0, 2);
      case 'md':
        return formIds.slice(0, 3);
      case 'lg':
        return formIds.slice(0, 4);
      default:
        return formIds.slice(0, 5);
    }
  }, [breakpoint, formIds]);

  useEffect(() => {
    getAdobeTargetOffer<{ formIds: string[] }>('a7_et')
      .then((data) => {
        setFormIds(data.formIds);
      })
      .catch(() => {
        setFormIds([]);
      });
  }, []);

  if (responsiveFormIds.length === 0 || !isOpened) {
    return null;
  }

  if (/mobile|sm/.test(breakpoint)) {
    return (
      <>
        {breakpoint === 'sm' && <Box pt={CONSTRAINT.SHEET.MOBILE_MAX_HEIGHT - 120} />}
        <MobileFormRecommendationSheet breakpoint={breakpoint} formIds={responsiveFormIds} />
      </>
    );
  }

  return (
    <>
      <Box pt={CONSTRAINT.SHEET.DESKTOP_MAX_HEIGHT - 120} />
      <DesktopFormRecommendationSheet
        breakpoint={breakpoint}
        formIds={responsiveFormIds}
        isOpenHandler={setIsOpened}
      />
    </>
  );
}

export default FormRecommendationSheet;
